import { createFileRoute, Link } from "@tanstack/react-router";
import { Heart, ShoppingBag, Trash2, Star, Sparkles } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { AccountLayout } from "@/components/account/AccountLayout";
import { products } from "@/lib/products";
import { useCart, useWishlist } from "@/lib/store";
import { API_BASE_URL } from "@/lib/api";

export const Route = createFileRoute("/account/wishlist")({
  head: () => ({
    meta: [
      { title: "Wishlist Sanctuary — Royal Lumina" },
      { name: "description", content: "Your saved handcrafted luxury candle rituals." },
    ],
  }),
  component: WishlistPage,
});

function WishlistPage() {
  const { ids, remove } = useWishlist();
  const { add } = useCart();

  const { data: dbProducts = products } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/products`);
        if (res.ok) return await res.json();
      } catch (err) {
        console.warn("Using static products fallback");
      }
      return products;
    }
  });

  const items = dbProducts.filter((p: any) => ids.includes(p.id));

  return (
    <AccountLayout title="Wishlist Sanctuary" subtitle={`${items.length} ${items.length === 1 ? "ritual candle" : "ritual candles"} saved in your collection.`}>
      {items.length === 0 ? (
        <div className="py-16 text-center bg-card rounded-[24px] border border-border/80 p-8 shadow-luxe space-y-5 max-w-xl mx-auto">
          <div className="mx-auto h-16 w-16 rounded-full bg-gold/10 grid place-items-center text-gold">
            <Heart className="h-8 w-8 stroke-[1.6]" />
          </div>
          <div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-primary">Your Wishlist is Resting</h3>
            <p className="mt-2 text-xs sm:text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
              Tap the heart icon on any handcrafted candle in our boutique to save it here for future rituals.
            </p>
          </div>
          <div className="pt-2">
            <Link to="/shop" className="btn-gold inline-flex h-11 px-8 text-xs uppercase tracking-wider font-semibold">
              Explore Collection
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {items.map((p: any) => (
            <div
              key={p.id}
              className="group bg-card border border-border/80 rounded-[20px] overflow-hidden shadow-luxe hover:border-gold/50 transition-all flex flex-col justify-between"
            >
              <div className="relative aspect-square overflow-hidden bg-secondary">
                <img
                  src={p.img}
                  alt={p.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  onError={(e) => { (e.target as HTMLImageElement).src = "/placeholder.svg"; }}
                />
                
                {p.tag && (
                  <span className="absolute top-3 left-3 text-[0.55rem] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full bg-primary/90 text-gold border border-gold/30">
                    {p.tag}
                  </span>
                )}

                <button
                  aria-label="Remove from wishlist"
                  onClick={() => remove(p.id)}
                  className="absolute top-3 right-3 h-8 w-8 rounded-full bg-background/90 text-foreground grid place-items-center hover:bg-destructive hover:text-white transition-colors shadow-sm"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                </button>
              </div>

              <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between space-y-3">
                <div>
                  <div className="flex items-center gap-1 text-gold text-xs">
                    <Star className="h-3.5 w-3.5 fill-gold" />
                    <span className="font-semibold text-primary">{p.rating || 5.0}</span>
                    <span className="text-[0.65rem] text-muted-foreground">({p.reviews || 12})</span>
                  </div>

                  <h3 className="font-display font-semibold text-base sm:text-lg text-primary truncate mt-1">
                    {p.name}
                  </h3>
                  
                  {p.category && (
                    <span className="text-[0.68rem] text-muted-foreground uppercase tracking-wider block mt-0.5">
                      {p.category}
                    </span>
                  )}
                </div>

                <div className="pt-2 border-t border-border/60 flex items-center justify-between">
                  <div>
                    <span className="font-display text-lg sm:text-xl font-bold text-primary">₹{p.price}</span>
                    {p.compareAt && (
                      <span className="text-xs text-muted-foreground line-through ml-1.5 font-mono">₹{p.compareAt}</span>
                    )}
                  </div>

                  <button
                    onClick={() => add(p.id)}
                    className="btn-gold h-9 px-3.5 text-xs font-semibold inline-flex items-center gap-1.5"
                  >
                    <ShoppingBag className="h-3.5 w-3.5" /> Add
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AccountLayout>
  );
}
